import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthProvider';
import type { UUID } from '@/types';

const TENANT_ID_KEY = 'voicera:tenant:id';
const TENANT_OVERRIDE_KEY = 'voicera:tenant:override';

type TenantOverride = { id: UUID; name: string; number: number };

type TenantContextValue = {
  /** Canonical UUID for internal matching — null when platform admin has no active tenant */
  tenantId: UUID | null;
  /** Simple numeric id for API paths and display (1, 2, 3…) */
  tenantNumber: number;
  tenantName: string;
  /** True when a tenant is available (session tenant or platform override) */
  hasTenant: boolean;
  setActiveTenant: (tenantId: UUID, tenantName: string, tenantNumber?: number) => void;
  clearActiveTenant: () => void;
};

const TenantContext = createContext<TenantContextValue | null>(null);

type TenantProviderProps = {
  children: ReactNode;
};

function readStoredOverride(): TenantOverride | null {
  try {
    const raw = localStorage.getItem(TENANT_OVERRIDE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TenantOverride;
    if (parsed?.id && parsed?.name) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

/** Active tenant context for API scoping and platform admin tenant switching. */
export function TenantProvider({ children }: TenantProviderProps) {
  const { session } = useAuth();
  const [override, setOverride] = useState<TenantOverride | null>(() => readStoredOverride());

  useEffect(() => {
    if (session?.tenant?.id) {
      localStorage.setItem(TENANT_ID_KEY, session.tenant.id);
      // Session has a real tenant — drop stale platform override
      setOverride(null);
      localStorage.removeItem(TENANT_OVERRIDE_KEY);
    } else if (!session?.is_platform_admin && session?.user.role !== 'VOICERA_ADMIN') {
      setOverride(null);
      localStorage.removeItem(TENANT_OVERRIDE_KEY);
    }
  }, [session?.tenant?.id, session?.is_platform_admin, session?.user.role]);

  const setActiveTenant = useCallback(
    (tenantId: UUID, tenantName: string, tenantNumber?: number) => {
      const next: TenantOverride = {
        id: tenantId,
        name: tenantName,
        number: tenantNumber ?? 0,
      };
      setOverride(next);
      localStorage.setItem(TENANT_ID_KEY, tenantId);
      localStorage.setItem(TENANT_OVERRIDE_KEY, JSON.stringify(next));
    },
    []
  );

  const clearActiveTenant = useCallback(() => {
    setOverride(null);
    localStorage.removeItem(TENANT_OVERRIDE_KEY);
    if (session?.tenant?.id) {
      localStorage.setItem(TENANT_ID_KEY, session.tenant.id);
    } else {
      localStorage.removeItem(TENANT_ID_KEY);
    }
  }, [session?.tenant?.id]);

  const value = useMemo<TenantContextValue>(() => {
    if (!session) {
      return {
        tenantId: null,
        tenantNumber: 0,
        tenantName: '',
        hasTenant: false,
        setActiveTenant,
        clearActiveTenant,
      };
    }

    const fromSession = session.tenant
      ? {
          id: session.tenant.id,
          name: session.tenant.name,
          number: session.tenant.tenant_number ?? 0,
        }
      : null;

    const active = override ?? fromSession;

    return {
      tenantId: active?.id ?? null,
      tenantNumber: active?.number ?? 0,
      tenantName: active?.name ?? (session.is_platform_admin ? 'Platform' : ''),
      hasTenant: !!active,
      setActiveTenant,
      clearActiveTenant,
    };
  }, [session, override, setActiveTenant, clearActiveTenant]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}

/** Tenant id for API paths — prefers simple numeric id (1, 2, 3). Empty when no tenant. */
export function useTenantId(): string {
  const { tenantNumber, tenantId, hasTenant } = useTenant();
  if (!hasTenant || !tenantId) return '';
  return tenantNumber > 0 ? String(tenantNumber) : tenantId;
}
